/**
 * Tenant-configurable HIRA severity/probability scale labels.
 *
 * Display wording only — the 1-5 integers stay canonical. Labels come from
 * GET /hira/scale-label-config; DEFAULT_SCALE_LABELS mirrors the backend
 * defaults (services/hira_scale_labels.py — client EHSMS-F-12 wording) so
 * unconfigured tenants render identically before the fetch resolves.
 */
import axios from 'axios';
import { API } from '../App';

export const DEFAULT_SCALE_LABELS = {
  severity: [
    { value: 1, label: 'Insignificant', description: 'Non-reportable, superficial injuries; nuisance, irritation, discomfort' },
    { value: 2, label: 'Minor', description: 'First aid required (minor burn, insect bite, nose bleed), minor cuts, bruises' },
    { value: 3, label: 'Moderate', description: 'Multiple injuries, minor fractures, serious sprains; ill-health leading to permanent minor disability' },
    { value: 4, label: 'Major', description: 'Serious injury requiring specialist medical treatment or hospitalization; severe life-shortening disease' },
    { value: 5, label: 'Critical', description: 'Fatal accident (loss of life, death, permanent disabling injury)' },
  ],
  probability: [
    { value: 1, label: 'Rare', description: 'Rarely happens within 1 Year' },
    { value: 2, label: 'Unlikely', description: 'Unlikely to happen within 6 months' },
    { value: 3, label: 'Possible', description: 'May happen within 1 month' },
    { value: 4, label: 'Likely', description: 'May happen within 1 Week' },
    { value: 5, label: 'Almost Certain', description: 'May happen daily' },
  ],
};

let _cache = null;
let _pending = null;

/**
 * Fetch the tenant scale-label config (cached per page load).
 * Returns {severity, probability, is_customized, default_labels, can_manage}.
 * Falls back to defaults (can_manage=false — fail closed) on error.
 */
export const fetchScaleLabelConfig = async (force = false) => {
  if (_cache && !force) return _cache;
  if (_pending && !force) return _pending;
  _pending = axios
    .get(`${API}/hira/scale-label-config`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
    .then((res) => {
      _cache = res.data;
      return _cache;
    })
    .catch(() => ({
      ...DEFAULT_SCALE_LABELS,
      is_customized: false,
      default_labels: DEFAULT_SCALE_LABELS,
      can_manage: false,
    }))
    .finally(() => {
      _pending = null;
    });
  return _pending;
};

/** Invalidate the cache (call after saving label config). */
export const clearScaleLabelCache = () => {
  _cache = null;
};
